/**
 * Deterministic 32-bit positive integer hash of a string user id, suitable
 * for use as an Agora UID. Result is in [1, 2^31 - 1].
 *
 * Uses FNV-1a 32-bit (Fowler–Noll–Vo). The exact same algorithm is
 * implemented in backend/server.py as `uid_from_user_id`. Both must stay
 * in sync — change them together.
 *
 * Why deterministic? Agora's `uid: 0` makes the server pick a random uid
 * per join. With deterministic UIDs derived from `user.id`, every client
 * (host, co-host, anonymous viewer) can compute the same uid → identity
 * mapping locally, eliminating the need for a backend round-trip.
 */
export function uidFromUserId(userId) {
  if (!userId) return 1;
  const str = String(userId);
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i) & 0xff;
    // FNV prime 0x01000193. Math.imul gives true 32-bit multiplication.
    hash = Math.imul(hash, 0x01000193);
  }
  // Mask to 31 bits — Agora uids are signed 32-bit and we want positive.
  // Agora reserves uid 0 for server-assignment, so we never return 0.
  hash = hash & 0x7fffffff;
  return hash === 0 ? 1 : hash;
}
